import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axios from "axios";
import { motion } from "framer-motion";
import SpendingHistory from "./SpendingHistory";
import SpendingCategories from "./SpendingCategories";

function Dashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("burndown-chart");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dailySpendingNorm, setDailySpendingNorm] = useState(0);

  const calculateDailySpendingNorm = (plannedSpending) => {
    if (!plannedSpending || plannedSpending.length < 2) {
      return 0;
    }
    const startBalance = plannedSpending[0].balance;
    const endBalance = plannedSpending[plannedSpending.length - 1].balance;
    const numberOfDays = plannedSpending.length - 1;
    if (numberOfDays === 0) {
      return 0;
    }
    const dailyNorm = (startBalance - endBalance) / numberOfDays;
    return dailyNorm.toFixed(2);
  };

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (["spending-history", "spending-categories"].includes(path)) {
      setActiveTab(path);
    } else {
      setActiveTab("spending-history");
      navigate("/dashboard/spending-history", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Load the last generated chart on mount
  useEffect(() => {
    const loadLatestChart = async () => {
      try {
        const response = await axios.get("/api/burndown-chart/latest");
        const { startDate, endDate, actualSpending, plannedSpending } =
          response.data;

        // Set the dates
        setDateRange([new Date(startDate), new Date(endDate)]);

        // Format and set the chart data
        const formattedData = actualSpending.map((actual, index) => ({
          name: new Date(actual.date).toLocaleDateString(),
          actual: actual.balance,
          planned: plannedSpending[index] ? plannedSpending[index].balance : 0,
        }));

        setChartData(formattedData);
        setDailySpendingNorm(calculateDailySpendingNorm(plannedSpending));
        console.log("Loaded latest chart:", {
          startDate,
          endDate,
          dataPoints: formattedData.length,
        });
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error("Error loading latest chart:", error);
        }
        // If no chart exists, that's okay - user will generate one
      }
    };

    loadLatestChart();
  }, []);

  const handleGenerateChart = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching burndown chart data...", { startDate, endDate });
      const response = await axios.get(
        `/api/burndown-chart?startDate=${startDate?.toISOString()}&endDate=${endDate?.toISOString()}`
      );
      console.log("Response data:", response.data);

      const { actualSpending, plannedSpending } = response.data;

      const formattedData = actualSpending.map((actual, index) => ({
        name: new Date(actual.date).toLocaleDateString(),
        actual: actual.balance,
        planned: plannedSpending[index] ? plannedSpending[index].balance : 0,
      }));

      console.log("Formatted chart data:", formattedData);
      setChartData(formattedData);
      setDailySpendingNorm(calculateDailySpendingNorm(plannedSpending));
    } catch (error) {
      console.error("Error fetching burndown chart data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl lg:max-w-5xl mx-auto space-y-6"
    >
      {activeTab === "burndown-chart" && (
        <div className="space-y-6">
          {/* Controls Card */}
          <div className="card bg-white dark:bg-dark-surface backdrop-blur-sm relative z-30 transition-colors">
            <div className="flex flex-col space-y-4">
              <div className="w-full">
                <label
                  htmlFor="dateRange"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t("dateRange", "Діапазон дат")}
                </label>
                <div className="relative z-50">
                  <DatePicker
                    selected={startDate}
                    onChange={(update) => {
                      setDateRange(update);
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    popperPlacement="bottom-end"
                    className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-xl focus:ring-primary-500 focus:border-primary-500 block p-3 transition-colors"
                    placeholderText="Select date range"
                  />
                </div>
              </div>
              
              <button
                onClick={handleGenerateChart}
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3 text-base font-medium"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("generating")}
                  </>
                ) : (
                  t("generateChart")
                )}
              </button>
            </div>
          </div>

          {/* Stats Card */}
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800 rounded-2xl p-6 text-white shadow-lg shadow-primary-500/25 dark:shadow-primary-900/30"
          >
            {/* Subtle decorative circles */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-white/10 rounded-full" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/5 rounded-full" />

            {/* Content */}
            <div className="relative">
              <p className="text-white/80 text-sm font-medium mb-1">{t("dailySpendingNorm")}</p>
              <p className="text-3xl font-heading font-bold text-white">
                {dailySpendingNorm} <span className="text-lg font-normal text-white/70">EUR</span>
              </p>
            </div>
          </motion.div>

          {/* Chart Card */}
          <div className="card min-h-[400px] flex flex-col">
            {isLoading ? (
              <div className="flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="animate-spin h-10 w-10 text-primary-500 mx-auto mb-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <p className="text-gray-500 dark:text-gray-400 font-medium">{t("loadingChart")}</p>
                </div>
              </div>
            ) : (
              <div className="w-full h-[400px] -ml-4">
                <LineChart
                  width={window.innerWidth < 768 ? window.innerWidth - 48 : 700} // Responsive width hack
                  height={400}
                  data={chartData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="name"
                    fontSize={10}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    tick={{ fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    fontSize={10} 
                    tick={{ fill: '#64748b' }} 
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      borderRadius: '12px', 
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      border: 'none',
                      padding: '12px'
                    }} 
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line
                    type="monotone"
                    dataKey="planned"
                    stroke="#ef4444"
                    strokeWidth={3}
                    dot={false}
                    name={t("planned")}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={false}
                    name={t("actual")}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === "spending-history" && <SpendingHistory />}
      {activeTab === "spending-categories" && <SpendingCategories />}
    </motion.div>
  );
}

export default Dashboard;
